import React, { useState } from "react";
import UniversalDataInspector from "../../../common/parsers/UniversalDataInspector";

/**
 * 🕵️ WORKFLOW VALIDATION VIEW
 * Purpose: Renders the "Single Pot" integrated JSON (Business + Finance) for user inspection.
 */
export default function WorkflowValidationView({
  selectedFile,
  results,
  isProcessing,
  onValidate,
}) {
  const [expandedSheet, setExpandedSheet] = useState(null);
  const [treeData, setTreeData] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  /**
   * 🎯 THE HIERARCHY HANDOFF
   * Accesses the 'esgTree' pre-computed by the TaxonomyReconstructor.
   * This contains the relational join: Presentation + Calculation Map.
   */
  const handleGenerateTree = () => {
    setIsGenerating(true);
    
    // Small timeout to allow UI state to reflect the "Mapping" phase
    setTimeout(() => {
      const schema = results?.esgTree;

      if (!schema || !schema.concepts) {
        console.error("❌ [VIEW] Error: esgTree missing from results.", results);
        alert("Validation data not found. Please run validation first.");
        setIsGenerating(false);
        return;
      }

      // This is the "New Tree" (The Single Pot)
      setTreeData(schema);
      setIsGenerating(false);
      console.log("🌲 [VIEW] Integrated ESG Tree loaded for inspection.");
    }, 500);
  };

  const handlePrintHierarchy = () => {
    if (!treeData) return;
    const printWindow = window.open("", "_blank");
    const printContent = `
    <html>
      <head><title>ESRS Taxonomy Hierarchy - ${selectedFile?.filename}</title></head>
      <body style="font-family: monospace; padding: 40px;">
        <h1>ESRS Taxonomy Hierarchy Report</h1>
        <hr />
        <pre>${JSON.stringify(treeData, null, 2)}</pre>
        <script>window.onload = function() { window.print(); window.close(); };</script>
      </body>
    </html>`;
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  const handleFinalSave = async () => {
    console.log("💾 [SAVE] Committing Integrated Tree to Storage:", treeData);
    alert("ESG Taxonomy Workflow Concluded and Saved Successfully.");
  };

  if (!selectedFile) {
    return (
      <div className="p-5 text-center text-muted small italic bg-light rounded">
        Select an asset from the inventory to begin structural analysis.
      </div>
    );
  }

  return (
    <div className="card shadow-sm border-0 bg-white overflow-hidden animate__animated animate__fadeIn">
      <div className="card-header bg-white py-2 border-bottom d-flex justify-content-between align-items-center">
        <h6 className="mb-0 fw-bold text-primary small">Structural Analysis</h6>
        <span className="badge bg-dark font-monospace" style={{ fontSize: "10px" }}>
          {selectedFile?.filename}
        </span>
      </div>

      <div className="card-body p-0">
        {!results ? (
          <div className="text-center py-5">
            <button className="btn btn-primary btn-sm fw-bold px-5" onClick={onValidate} disabled={isProcessing}>
              {isProcessing ? "ANALYZING..." : "START VALIDATION"}
            </button>
          </div>
        ) : (
          <div className="animate__animated animate__fadeIn">
            {/* 1. RAW EXCEL INSPECTION */}
            <div className="worksheet-list">
              {results.worksheets?.map((ws, i) => (
                <div key={i} className="border-bottom">
                  <div
                    className={`d-flex align-items-center px-3 py-1 cursor-pointer ${expandedSheet === ws.name ? "bg-light" : ""}`}
                    onClick={() => setExpandedSheet(expandedSheet === ws.name ? null : ws.name)}
                  >
                    <div className="flex-grow-1 small fw-bold">{ws.name}</div>
                    <div className="text-muted extra-small">{ws.rowCount} Rows</div>
                  </div>
                  {expandedSheet === ws.name && (
                    <div className="p-2 border-top bg-white">
                      <UniversalDataInspector worksheet={ws} />
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* 2. TRIGGER THE SINGLE POT PREVIEW */}
            <div className="p-3 bg-white border-top d-grid">
              <button
                className="btn btn-primary fw-bold"
                onClick={handleGenerateTree}
                disabled={isGenerating || treeData}
              >
                {isGenerating ? "MAPPING RELATIONSHIPS..." : "GENERATE ESG TREE"}
              </button>
            </div>

            {/* 3. INTEGRATED JSON INSPECTION WINDOW */}
            {treeData && (
              <div className="p-3 bg-light border-top animate__animated animate__fadeInUp">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="extra-small fw-bold text-uppercase text-primary mb-0">
                    Integrated ESG Taxonomy (Business + Finance Face)
                  </h6>
                  <button className="btn btn-outline-dark btn-sm extra-small py-0" onClick={handlePrintHierarchy}>
                    <i className="bi bi-printer me-1"></i> PRINT B&W
                  </button>
                </div>

                <div className="rounded border bg-dark p-3 mb-3" style={{ maxHeight: "400px", overflowY: "auto" }}>
                  <pre className="text-success mb-0" style={{ fontSize: "10px", lineHeight: "1.4" }}>
                    {/* 🎯 THIS IS THE DUMP YOU WANTED: FULL RELATIONAL JSON */}
                    {JSON.stringify(treeData, null, 2)}
                  </pre>
                </div>

                <div className="d-grid">
                  <button className="btn btn-success btn-lg fw-bold shadow" onClick={handleFinalSave}>
                    SAVE & CONCLUDE WORKFLOW <i className="bi bi-cloud-check ms-2"></i>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}